import { z } from 'zod';
import { HttpUrl } from './common/url.js';
/**
 * The star ladder (FR-SUBS-04). Tier index 0..9. Prices in minor units,
 * per month; the D-01 conflict resolved as docs/01 recommends: Starter is
 * FREE and the paid ladder starts at 1★. Above 3★ requires admin approval,
 * not payment alone (FR-SUBS-09).
 */
export const TIERS = [
    { index: 0, key: 'starter', name: 'Starter Profile', stars: 0, priceMinor: 0 },
    { index: 1, key: 'star1', name: '1 Star Profile', stars: 1, priceMinor: 999 },
    { index: 2, key: 'star2', name: '2 Star Profile', stars: 2, priceMinor: 1_499 },
    { index: 3, key: 'star3', name: '3 Star Profile', stars: 3, priceMinor: 1_999 },
    { index: 4, key: 'star4', name: '4 Star Profile', stars: 4, priceMinor: 2_499 },
    { index: 5, key: 'star5', name: '5 Star Profile', stars: 5, priceMinor: 2_999 },
    { index: 6, key: 'black1', name: 'Black Diamond 5 Star', stars: 5, priceMinor: 3_999 },
    { index: 7, key: 'black2', name: 'Double Black Diamond 5 Star', stars: 5, priceMinor: 4_999 },
    {
        index: 8,
        key: 'black3',
        name: 'Triple Crown Black Diamond 5 Star',
        stars: 5,
        priceMinor: 5_999,
    },
    {
        index: 9,
        key: 'black4',
        name: 'Quadruple Crown Black Diamond 5 Star',
        stars: 5,
        priceMinor: 6_999,
    },
];
export const TierKey = z.enum(TIERS.map((t) => t.key));
/** Tiers strictly above this index require admin approval (FR-SUBS-09). */
export const ADMIN_GATED_ABOVE_INDEX = 3;
export const TierState = z.enum(['achieved', 'current', 'choose', 'locked']);
export const TierView = z.object({
    index: z.number().int(),
    key: TierKey,
    name: z.string(),
    stars: z.number().int(),
    priceMinor: z.number().int(),
    state: TierState,
    /** Present when locked — the "why" (AC-SUBS-04.1). */
    lockReason: z.string().nullable(),
    /** Supporter threshold for this tier (Q-13, FR-ADM-13). */
    supporterThreshold: z.number().int(),
    templateCount: z.number().int(),
});
export const SubscriptionView = z.object({
    currentTier: TierKey,
    currentIndex: z.number().int(),
    renewsAt: z.coerce.date().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    /** Paid a jump (FR-SUBS-03) and waiting on the supporter threshold (G-14). */
    pendingJumpTo: TierKey.nullable(),
    /** Admin-approved eligibility above 3★, if any (FR-SUBS-09/ADM-11). */
    approvedUpTo: TierKey.nullable(),
    ladder: z.array(TierView),
});
export const ChooseTierInput = z.object({
    tier: TierKey,
    methodId: z.string().optional(),
    idempotencyKey: z.string().min(8).max(100),
});
export const UpgradeRequestInput = z.object({
    reason: z.string().trim().min(20).max(2_000),
    documentUrls: z.array(HttpUrl).min(1).max(5),
});
export const UpgradeRequestView = z.object({
    id: z.string(),
    currentLevel: z.string(),
    requestedTier: TierKey,
    state: z.enum(['pending', 'approved', 'rejected', 'auto_closed']),
    decisionReason: z.string().nullable(),
    createdAt: z.coerce.date(),
});
export const TemplateView = z.object({
    id: z.string(),
    name: z.string(),
    previewUrl: z.string(),
    /** Minimum tier index that unlocks it (FR-SUBS-07). */
    minTierIndex: z.number().int(),
    unlocked: z.boolean(),
    selected: z.boolean(),
});
//# sourceMappingURL=subscription.js.map